import React from 'react';
import { useStaticQuery, graphql } from 'gatsby';

import PageLayout from './PageLayout';
import SideMenu from './SideMenu';
import { useTranslation } from 'react-i18next';
import GenericLink from './GenericLink';
import nerthusLogo from '../assets/images/nerthusLogo.svg';
import { NERTHUS_LINK, NERTHUS_LINKS_ENABLED } from '../constants';

import * as styles from './ResourcesPageLayout.module.scss';

interface ResourcesPageLayoutProps {
  currentSlug: string;
  withPageContentBox?: boolean;
  searchBox?: React.ReactNode;
  children: React.ReactNode;
}

const ResourcesPageLayout = ({
  currentSlug,
  withPageContentBox,
  searchBox,
  children,
}: ResourcesPageLayoutProps): React.ReactElement => {
  const { t } = useTranslation();
  const data: any = useStaticQuery(graphql`
    fragment ArticleSection on SanityArticleSection {
      _key
      title
      slug {
        current
      }
    }

    fragment ArticlesSection on SanityArticlesSection {
      _key
      title
      slug {
        current
      }
    }

    fragment FilesSection on SanityFilesSection {
      title
      _key
      slug {
        current
      }
    }
    {
      sanityResourcesPage {
        articlesAndFilesSections {
          __typename
          ...ArticleSection
          ...ArticlesSection
          ...FilesSection

          ... on SanitySubMenuSections {
            title
            articlesAndFilesSections {
              ...ArticleSection
              ...ArticlesSection
              ...FilesSection
            }
          }
        }
        linksSection {
          __typename
        }
      }
      allSanityResourceVideo {
        totalCount
      }
    }
  `);

  const { sanityResourcesPage: resourcesPage, allSanityResourceVideo: allResourceVideos } = data;

  const entries = [
    ...(resourcesPage?.articlesAndFilesSections
      ? resourcesPage.articlesAndFilesSections.map((entry: any) => {
          if (entry.__typename === 'SanitySubMenuSections') {
            return {
              title: entry.title,
              entries: entry.articlesAndFilesSections.map((subMenuEntry: any) => ({
                title: subMenuEntry.title,
                url: '/resources/' + subMenuEntry.slug.current,
                isActive: currentSlug === subMenuEntry.slug.current,
              })),
            };
          } else {
            return {
              title: entry.title,
              url: '/resources/' + entry.slug.current,
              isActive: currentSlug === entry.slug.current,
            };
          }
        })
      : []),
    ...(allResourceVideos.totalCount > 0
      ? [
          {
            title: t('videos_page.title', 'Videos'),
            url: '/resources/videos',
            isActive: currentSlug === 'videos',
          },
        ]
      : []),
    ...(resourcesPage.linksSection?.length > 0
      ? [
          {
            title: t('useful_links_page.title', 'Useful Links'),
            url: '/resources/useful-links',
            isActive: currentSlug === 'useful-links',
          },
        ]
      : []),
  ];

  return (
    <PageLayout
      title={t('resources_page.title', 'Resources')}
      sideBarChildren={
        <>
          <SideMenu entries={entries}></SideMenu>
          {NERTHUS_LINKS_ENABLED && (
            <div className={styles.nerthusContainer}>
              <GenericLink className={styles.nerthusButton} noStyle to={NERTHUS_LINK}>
                {t('nerthus.project_name', 'Nerthus Sustainability')}
              </GenericLink>
              <p className={styles.nerthusInfo}>
                {t('nerthus.info', 'Sustainability and Social Responsibility')}
              </p>
            </div>
          )}
        </>
      }
      withPageContentBox={withPageContentBox}
      searchBox={searchBox}
    >
      {children}
    </PageLayout>
  );
};

export default ResourcesPageLayout;
